/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { WeatherCodeHeader } from './WeatherCodeHeader.component';
import { Geopoint } from 'pleinchamp-api-client';
import React, { MutableRefObject, useRef } from 'react';
import Sticky from 'react-stickynode';
import { IconButton } from '@components/buttons/IconButton/IconButton.component';
import { ChevronLeftIcon } from '@components/icons/ChevronLeft.icon';
import { ChevronRightIcon } from '@components/icons/ChevronRight.icon';
import { PlcTextListLoader } from '@components/Loaders/PlcTextLoader.component';
import { Text } from '@components/Text/Text.component';
import { TextProps } from '@components/Text/Text.types';
import { PlcAnchor } from '@layout/components/PlcAnchor/PlcAnchor.component';
import { PlcColor } from '@styles/colors';
import { usePlcRouter } from '@utils/customHooks';
import { formatDateShortDayAndDayNumber, formatDateToHour } from '@utils/date';
import { Dayjs } from 'dayjs';
import { Direction, getScroll } from '../utils';
import { buildDayLabel, getHourByHourRedirection, isSafari } from './Header.utils';

import './WeatherTableHeader.scss';

interface Props {
  dates: Dayjs[];
  weatherCodes: Array<number | undefined>;
  hoveredIndex?: number;
  format15Days?: boolean;
  setHoveredIndex: (index: number | undefined) => void;
  withDays?: boolean;
  handleNavigation: (dir: Direction) => void;
  isLoading?: boolean;
  bottomBoundary?: number;
  top?: number;
  overFlowingContainerRef: MutableRefObject<HTMLDivElement | null>;
  coordinates: Geopoint;
}

const TEXT_PROPS: TextProps[] = [{ variant: 'h5', width: 25 }];
const EMPTY: null[] = [];

function HeaderContent({
  dates,
  weatherCodes,
  hoveredIndex,
  setHoveredIndex,
  withDays,
  handleNavigation,
  format15Days,
  isLoading,
  overFlowingContainerRef,
  coordinates,
}: Omit<Props, 'bottomBoundary'>) {
  const displayDays = withDays && !isLoading;
  const days = displayDays ? buildDayLabel(dates) : EMPTY;

  const scrollLeft = getScroll(overFlowingContainerRef, Direction.Left);
  const scrollRight = getScroll(overFlowingContainerRef, Direction.Right);

  const {
    query: { locality },
  } = usePlcRouter();

  return (
    <>
      <tr className="empty">
        <th rowSpan={displayDays ? 4 : 3} />
        <th className="table-navigation left" />
        {dates.map((_, i) => (
          <th key={i} className={hoveredIndex === i ? 'emphasis' : ''} onMouseEnter={() => setHoveredIndex(i)} />
        ))}
        <th className="table-navigation right" />
      </tr>
      {displayDays && (
        <tr className="days">
          <th className="table-navigation left" />
          {days.map((day, i) => (
            <th key={i} className={hoveredIndex === i ? 'emphasis' : ''} onMouseEnter={() => setHoveredIndex(i)}>
              {day && <Text flavour="grey" i18nKey={day} tag="span" variant="tiny-title" />}
            </th>
          ))}
          <th className="table-navigation right" />
        </tr>
      )}
      <tr className="dates">
        <th className="table-navigation left">
          <IconButton onClick={() => handleNavigation(Direction.Left)}>
            <ChevronLeftIcon fillColor={scrollLeft ? undefined : PlcColor.GREY_CLOUD_MEDIUM} width={8} />
          </IconButton>
        </th>
        {dates.map((d, i) => (
          <th key={i} className={hoveredIndex === i ? 'emphasis' : ''} onMouseEnter={() => setHoveredIndex(i)}>
            {isLoading ? (
              <PlcTextListLoader fixedWidth textProps={TEXT_PROPS} uniqueKey={`WeatherTableHeaderDate-${i}`} />
            ) : (
              <PlcAnchor linkProps={getHourByHourRedirection(locality, dates[i])}>
                <Text className={`${format15Days && 'table-navigation-date-15-days'}`} tag="span" variant="h5">
                  {format15Days ? formatDateShortDayAndDayNumber(d) : formatDateToHour(d)}
                </Text>
              </PlcAnchor>
            )}
          </th>
        ))}
        <th className="table-navigation right">
          <IconButton onClick={() => handleNavigation(Direction.Right)}>
            <ChevronRightIcon fillColor={scrollRight ? undefined : PlcColor.GREY_CLOUD_MEDIUM} width={8} />
          </IconButton>
        </th>
      </tr>
      <tr className="weatherCode">
        <th className="table-navigation left" />
        {weatherCodes.map((wc, i) => (
          <WeatherCodeHeader
            key={i}
            code={wc}
            coordinates={coordinates}
            date={dates[i]}
            forceDay={Boolean(format15Days)}
            isEmphasis={i === hoveredIndex}
            isLoading={isLoading}
            locality={locality as string}
            onMouseOver={() => setHoveredIndex(i)}
          />
        ))}
        <th className="table-navigation right" />
      </tr>
    </>
  );
}

function WeatherTableHeader({ top, overFlowingContainerRef, format15Days, dates, ...props }: Props) {
  const headerTableRef = useRef<Sticky>(null);
  // @ts-ignore
  const innerElementRef: HTMLDivElement | undefined = headerTableRef?.current?.innerElement;

  function forceScroll() {
    if (overFlowingContainerRef?.current) {
      innerElementRef?.scroll({ behavior: 'auto', left: overFlowingContainerRef?.current.scrollLeft });
    }
  }

  return (
    <Sticky
      ref={headerTableRef}
      bottomBoundary=".overflowing-container"
      enableTransforms={!isSafari()}
      innerZ={2}
      onStateChange={forceScroll}
      top={top || '.plc-header'}
    >
      <table onMouseLeave={() => props.setHoveredIndex(undefined)}>
        <thead>
          <HeaderContent
            dates={dates}
            format15Days={format15Days}
            overFlowingContainerRef={overFlowingContainerRef}
            {...props}
          />
        </thead>
      </table>
    </Sticky>
  );
}

export { WeatherTableHeader };
